import React from 'react';
import PropTypes from 'prop-types';
import { IconText } from 'components/core/IconText';

import './FacilityAmenitiesRanked.scss';

// import all Amenity icons
import twentyfourhouraccess from 'assets/images/features-amenities/24-hr-access.svg';
import boat from 'assets/images/features-amenities/boat.svg';
import car from 'assets/images/features-amenities/car.svg';
import climatecontrolled from 'assets/images/features-amenities/climate-controlled.svg';
import driveupaccess from 'assets/images/features-amenities/drive-up-access.svg';
import fencedlit from 'assets/images/features-amenities/fenced-lit.svg';
import firstflooraccess from 'assets/images/features-amenities/first-floor-access.svg';
import freeuseoftruck from 'assets/images/features-amenities/free-use-of-truck.svg';
import onsiteoffice from 'assets/images/features-amenities/on-site-office.svg';
import residentmanager from 'assets/images/features-amenities/resident-manager.svg';
import rv from 'assets/images/features-amenities/rv.svg';
import surveillancecameras from 'assets/images/features-amenities/surveillance-cameras.svg';

// Define the ranking order for the icon and text display
const AMENITY_CONFIG = [
	{
		key: '24HrAccess',
		icon: twentyfourhouraccess,
		text: '24-hour access',
		alt: '24-hour-access-icon',
	},
	{
		key: 'onsiteOfficeAtFacility',
		icon: onsiteoffice,
		text: 'On-site office',
		alt: 'On-site-office-icon',
	},
	{
		key: 'surveillance',
		icon: surveillancecameras,
		text: 'Video cameras on site',
		alt: 'Video-cameras-on-site-icon',
	},
	{
		key: 'fenced',
		icon: fencedlit,
		text: 'Fenced & Lighted',
		alt: 'Fenced-&-Lighted-icon',
	},
	{
		key: 'freeTruckRental',
		icon: freeuseoftruck,
		text: 'Free use of truck',
		alt: 'Free-use-of-truck-icon',
	},
	{
		key: 'residentManager',
		icon: residentmanager,
		text: 'Resident manager',
		alt: 'Resident-manager-icon',
	},
	{
		key: 'climateControlled',
		icon: climatecontrolled,
		text: 'Climate controlled units',
		alt: 'Climate-controlled-units-icon',
	},
	{
		key: 'firstFloorAccess',
		icon: firstflooraccess,
		text: 'First floor access',
		alt: 'First-floor-access-icon',
	},
	{
		key: 'driveUpAccess',
		icon: driveupaccess,
		text: 'Drive-up access',
		alt: 'Drive-up-access-icon',
	},
	{
		key: 'vehicleStorage',
		icon: car,
		text: 'Vehicle Storage',
		alt: 'Vehicle-storage-icon',
	},
	{
		key: 'boatStorage',
		icon: boat,
		text: 'Boat Storage',
		alt: 'Boat-storage-icon',
	},
	{ key: 'rvStorage', icon: rv, text: 'RV Storage', alt: 'RV-storage-icon' },
];

const ICON_MAPPING = Object.fromEntries(
	AMENITY_CONFIG.map(({ key, icon, text, alt }) => [
		key,
		{ icon, text, alt },
	]),
);

// Storage unit feature checkers
const checkStorageUnitFeatures = (storageUnits) => ({
	firstFloorAccess:
		storageUnits?.some((unit) =>
			unit.availableAmenities?.includes('1st Floor'),
		) || false,
	driveUpAccess:
		storageUnits?.some((unit) =>
			unit.availableAmenities?.includes('Drive Up Access'),
		) || false,
	climateControlled:
		storageUnits?.some((unit) =>
			unit.availableAmenities?.includes('Climate Controlled'),
		) || false,
});

// Storage type criteria based on size bucket dimensions
const STORAGE_TYPE_CRITERIA = {
	vehicleStorage: ['15x10', '20x10', '30x10'], // 150, 200 sqft
	boatStorage: ['25x10'], // 250 sqft
	rvStorage: ['35x10', '40x10', '50x10'], // 300, 350, 400, 500 sqft
};

// Pre-compute entries for performance (avoids recreating array on every function call)
const STORAGE_TYPE_ENTRIES = Object.entries(STORAGE_TYPE_CRITERIA);

// Helper function to determine vehicle type based on sizeBucket
// Example: "20' x 10'" becomes "20x10"
const getVehicleTypeFromSizeBucket = (sizeBucket) => {
	// Guard: return null for empty inputs
	if (!sizeBucket) return null;

	// Normalize size bucket format: "20' x 10'" => "20x10"
	// String() ensures safe handling of unexpected types
	const normalizedSize = String(sizeBucket)
		.replaceAll("'", '')
		.replaceAll(/\s/g, '')
		.toLowerCase();

	// Find the first storage type whose dimensions match the normalized size
	const matchedEntry = STORAGE_TYPE_ENTRIES.find(([, dimensions]) =>
		dimensions.some((dim) => normalizedSize.includes(dim)),
	);

	// Explicit null check for clarity and safety
	return matchedEntry ? matchedEntry[0] : null;
};

// Vehicle unit feature checkers
const checkVehicleUnitFeatures = (vehicleUnits) => ({
	vehicleStorage:
		vehicleUnits?.some(
			(unit) =>
				unit.unitType === 'parking_space' &&
				getVehicleTypeFromSizeBucket(unit.sizeBucket) ===
					'vehicleStorage',
		) || false,
	boatStorage:
		vehicleUnits?.some(
			(unit) =>
				unit.unitType === 'parking_space' &&
				getVehicleTypeFromSizeBucket(unit.sizeBucket) === 'boatStorage',
		) || false,
	rvStorage:
		vehicleUnits?.some(
			(unit) =>
				unit.unitType === 'parking_space' &&
				getVehicleTypeFromSizeBucket(unit.sizeBucket) === 'rvStorage',
		) || false,
	firstFloorAccess:
		vehicleUnits?.some((unit) =>
			unit.availableAmenities?.includes('1st Floor'),
		) || false,
	driveUpAccess:
		vehicleUnits?.some((unit) =>
			unit.availableAmenities?.includes('Drive Up Access'),
		) || false,
	climateControlled:
		vehicleUnits?.some((unit) =>
			unit.availableAmenities?.includes('Climate Controlled'),
		) || false,
});

export function FacilityAmenitiesRanked({
	groupedAmenities = {},
	storageUnits = [],
	vehicleUnits = [],
}) {
	// Early return if no data
	if (!groupedAmenities && !storageUnits?.length && !vehicleUnits?.length)
		return 'No amenities data available';

	const availableAmenities = new Set();

	// Collect amenities from groupedAmenities (facility-level)
	if (groupedAmenities) {
		Object.values(groupedAmenities).forEach((category) => {
			if (category?.items) {
				Object.entries(category.items).forEach(([key, item]) => {
					if (item?.hasAttribute && ICON_MAPPING[key]) {
						availableAmenities.add(key);
					}
				});
			}
		});
	}

	// Combine storageUnits and vehicleUnits feature logic
	const unitFeatures = storageUnits?.length
		? checkStorageUnitFeatures(storageUnits)
		: {};
	const vehicleFeatures = vehicleUnits?.length
		? checkVehicleUnitFeatures(vehicleUnits)
		: {};

	// Merge features: if any key is true, result is true
	const combinedFeatures = { ...unitFeatures };
	Object.entries(vehicleFeatures).forEach(([key, value]) => {
		combinedFeatures[key] = combinedFeatures[key] || value;
	});

	// Add available amenities
	Object.keys(combinedFeatures).forEach((amenityKey) => {
		if (combinedFeatures[amenityKey] && ICON_MAPPING[amenityKey]) {
			availableAmenities.add(amenityKey);
		}
	});

	// Filter and map amenities in ranking order
	const amenityList = AMENITY_CONFIG.filter(({ key }) =>
		availableAmenities.has(key),
	).map(({ key, icon, text, alt }) => ({ key, icon, text, alt }));

	// Don't render if no amenities to show
	if (amenityList.length === 0) return null;

	return (
		<div className="amenities-ranked-content">
			{amenityList.map(({ key, icon, text }) => (
				<IconText
					key={key}
					src={icon}
					alt={text}
					size={'standard'}
					weight={'standard'}
					color={'black'}
					lazyLoad={true}
				>
					{text}
				</IconText>
			))}
		</div>
	);
}

FacilityAmenitiesRanked.propTypes = {
	groupedAmenities: PropTypes.object,
	storageUnits: PropTypes.array,
	vehicleUnits: PropTypes.array,
};

export default FacilityAmenitiesRanked;
